import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["frame", "refreshLabel", "total", "succeeded", "inProgress", "failed"]

  static values = {
    maxConcurrent: { type: Number, default: 2 }
  }

  connect() {
    this.inFlight = 0
    this.pendingQueue = []
    this.frameTargets.forEach(frame => this.enqueueFrame(frame))
    this.processQueue()
  }

  disconnect() {
    this.pendingQueue = []
  }

  refreshAll() {
    if (this.frameTargets.length === 0) return;

    if (this.hasRefreshLabelTarget) {
      const button = this.refreshLabelTarget.closest("button")
      if (button) {
        button.disabled = true
        button.classList.add("opacity-50", "cursor-not-allowed")
      }
      this.refreshLabelTarget.textContent = "Refreshing all deployment statuses..."
    }
    this.frameTargets.forEach(frame => {
      this.enqueueFrame(frame, true)
    })
    this.processQueue()
  }

  refreshFrame(event) {
    const key = event.currentTarget.dataset.deploymentKey
    const frame = this.frameTargets.find(f => f.dataset.deploymentKey === key)
    if (frame) {
      this.enqueueFrame(frame, true)
      this.processQueue()
    }
  }

  enqueueFrame(frame, isRefresh = false) {
    const src = frame.dataset.src
    if (!src) return

    // Remove existing pending entry for this frame to avoid duplicates
    this.pendingQueue = this.pendingQueue.filter(item => item.frame !== frame)

    // Set busy state
    frame.setAttribute("aria-busy", "true")

    // Add to queue
    this.pendingQueue.push({ frame, src, isRefresh })
  }

  processQueue() {
    while (this.inFlight < this.maxConcurrentValue && this.pendingQueue.length > 0) {
      const { frame, src, isRefresh } = this.pendingQueue.shift()
      this.loadFrame(frame, src, isRefresh)
    }
  }

  loadFrame(frame, src, isRefresh = false) {
    this.inFlight++

    // Only add refresh=true when explicitly refreshing
    let url = src
    if (isRefresh) {
      const separator = src.includes("?") ? "&" : "?"
      url = src.includes("refresh=true") ? src : `${src}${separator}refresh=true`
    }

    fetch(url, {
      headers: {
        "Accept": "text/html",
        "Turbo-Frame": frame.id
      }
    })
      .then(response => {
        if (response.redirected && response.url.includes("/auth/login")) {
          window.location.reload()
          return
        }
        if (response.ok) {
          return response.text()
        }
        throw new Error(`HTTP ${response.status}`)
      })
      .then(html => {
        const parser = new DOMParser()
        const doc = parser.parseFromString(html, "text/html")
        const content = doc.body.firstElementChild
        if (content) {
          // content is itself a <turbo-frame>. Transfer its child nodes
          // into the existing frame to avoid nested <turbo-frame> elements.
          const frag = document.createDocumentFragment()
          Array.from(content.childNodes).forEach(child => frag.appendChild(child))
          frame.replaceChildren(frag)
        }
        frame.setAttribute("aria-busy", "false")
        this.updateSummary()
      })
      .catch(error => {
        console.error(`Failed to load deployment status for ${frame.dataset.deploymentKey}:`, error)
        frame.setAttribute("aria-busy", "false")
        frame.innerHTML = `<div class="text-sm text-red-600">Failed to load deployment status.</div>`
        this.updateSummary()
      })
      .finally(() => {
        this.inFlight--
        if (this.pendingQueue.length === 0 && this.inFlight === 0) {
          if (this.hasRefreshLabelTarget) {
            const button = this.refreshLabelTarget.closest("button")
            if (button) {
              button.disabled = false
              button.classList.remove("opacity-50", "cursor-not-allowed")
            }
            this.refreshLabelTarget.textContent = "Refresh All"
          }
        }
        this.processQueue()
      })
  }

  updateSummary() {
    let succeeded = 0
    let inProgress = 0
    let failed = 0

    this.frameTargets.forEach(frame => {
      const badge = frame.querySelector("[data-deployment-status]")
      if (!badge) return

      const status = badge.dataset.deploymentStatus

      switch (status) {
        case "deployed":
          succeeded++
          break
        case "building":
        case "running":
          inProgress++
          break
        case "build_failed":
          failed++
          break
      }
    })

    if (this.hasSucceededTarget) {
      this.succeededTarget.textContent = succeeded
    }
    if (this.hasInProgressTarget) {
      this.inProgressTarget.textContent = inProgress
    }
    if (this.hasFailedTarget) {
      this.failedTarget.textContent = failed
    }

  }
}
